import React, { useState, useEffect, useRef, useCallback } from 'react';
import { knowledgeBase, conferenceDates } from './data';

const getDaysDiff = (d) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(d); target.setHours(0, 0, 0, 0);
  return Math.round((target - today) / 86400000);
};

const formatDate = (d) => {
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

const getBadge = (diff, label) => {
  if (diff < 0) {
    if (label.toLowerCase().includes("call for")) {
      return `<span class="badge badge-open">📢 Open</span>`;
    }
    return `<span class="badge badge-past">✅ Completed</span>`;
  } else if (diff === 0) {
    return `<span class="badge badge-today">🔴 Today!</span>`;
  } else if (diff <= 7) {
    return `<span class="badge badge-soon">⚠️ In ${diff} day${diff === 1 ? '' : 's'}</span>`;
  } else {
    return `<span class="badge badge-open">🟢 In ${diff} days</span>`;
  }
};

const getDynamicDatesResponse = () => {
  let html = `<strong>📅 Important Dates — ICETTACS 2026</strong><br><br>`;
  conferenceDates.forEach(item => {
    const diff = getDaysDiff(item.date);
    html += `<div class="date-row">
            <span class="date-label">• ${item.label}:</span>
            <span class="date-val">${formatDate(item.date)}</span>
            ${getBadge(diff, item.label)}
        </div>`;
  });
  const today = new Date();
  html += `<br><em style="font-size:0.78rem;color:#64748b;">Live status as of ${today.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</em>`;
  return html;
};

const getBotResponse = (input) => {
  let userText = input.toLowerCase();
  let bestMatch = null;
  let maxMatches = 0;

  for (const item of knowledgeBase) {
    let matches = 0;
    for (const keyword of item.keywords) {
      if (userText.includes(keyword)) {
        matches++;
      }
    }
    if (matches > maxMatches) {
      maxMatches = matches;
      bestMatch = item.response;
    }
  }

  if (bestMatch) {
    if (bestMatch === "__DYNAMIC_DATES__") {
      return getDynamicDatesResponse();
    }
    return bestMatch;
  } else {
    return "I'm not completely sure about that. Try asking about <em>Important Dates</em>, <em>Paper Submission</em>, or <em>Conference Tracks</em>.";
  }
};

export default function Chatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [isVoiceEnabled, setIsVoiceEnabled] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [input, setInput] = useState('');
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [messages, setMessages] = useState([
    {
      sender: 'bot',
      text: `Hello! I'm the official virtual assistant for ICETTACS 2026. I can help you with important dates, conference tracks, paper submission, and organizing committee details.<br><br>How can I assist you?`,
      suggestions: ['Important Dates', 'Submission Guidelines', 'Conference Tracks', 'Organizing Committee']
    }
  ]);

  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);
  const synth = window.speechSynthesis;
  const isVoiceEnabledRef = useRef(isVoiceEnabled);

  useEffect(() => {
    isVoiceEnabledRef.current = isVoiceEnabled;
  }, [isVoiceEnabled]);

  const speakResponseProxy = useCallback((htmlText, force = false) => {
    if (!synth) return;
    if (!force && !isVoiceEnabledRef.current) return;

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlText;
    const cleanText = tempDiv.textContent || tempDiv.innerText || "";

    synth.cancel();
    const utterance = new SpeechSynthesisUtterance(cleanText);

    utterance.rate = 1.0;
    utterance.pitch = 1.2;

    let voices = synth.getVoices();
    let femaleVoice = voices.find(v =>
      v.name.includes('Female') ||
      v.name.includes('Zira') ||
      v.name.includes('Samantha') ||
      v.name.includes('Victoria') ||
      v.name.includes('Hazel')
    );
    if (!femaleVoice) {
      femaleVoice = voices.find(v => v.lang.startsWith('en') && (v.name.includes('Google') || v.name.includes('Microsoft')));
    }
    if (femaleVoice) {
      utterance.voice = femaleVoice;
    }

    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);

    synth.speak(utterance);
  }, [synth]);

  const handleSendMessageFinal = useCallback((textToSend) => {
    const message = textToSend.trim();
    if (!message) return;

    setMessages((prev) => [...prev, { sender: 'user', text: message }]);
    setInput('');
    setIsTyping(true);

    setTimeout(() => {
      const reply = getBotResponse(message);
      setMessages((prev) => [...prev, { sender: 'bot', text: reply }]);
      setIsTyping(false);

      speakResponseProxy(reply);
    }, 800 + Math.random() * 500);
  }, [speakResponseProxy]);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = false;

      recognition.onstart = () => {
        setIsRecording(true);
      };

      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInput('');
        handleSendMessageFinal(transcript);
      };

      recognition.onerror = (event) => {
        setIsRecording(false);
        if (event.error === 'not-allowed') {
          alert("Microphone access was blocked! Please click the camera/mic icon in your browser's URL bar to allow permissions.");
        } else {
          console.error("Mic error:", event.error);
        }
      };

      recognition.onend = () => {
        setIsRecording(false);
      };

      recognitionRef.current = recognition;
    }
  }, [handleSendMessageFinal]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isTyping]);

  const toggleChat = () => setIsOpen(!isOpen);

  const toggleVoice = () => {
    const newState = !isVoiceEnabled;
    setIsVoiceEnabled(newState);
    if (newState) {
      speakResponseProxy("Hello, I am your Assistant for ICETTACS 2026. How can I help you today?", true);
    } else {
      if (synth) synth.cancel();
      setIsSpeaking(false);
    }
  };

  const toggleMic = () => {
    if (!recognitionRef.current) {
      alert("Speech Recognition is not supported in your browser. Please try using Google Chrome or Edge.");
      return;
    }
    if (synth) synth.cancel();
    setIsSpeaking(false);

    if (isRecording) {
      recognitionRef.current.stop();
    } else {
      recognitionRef.current.start();
    }
  };


  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSendMessageFinal(input);
    }
  };

  const handleSuggestionClick = (text) => {
    handleSendMessageFinal(text);
  };

  return (
    <>
      {isOpen && (
        <div className="icettacs-chat-container">
          <div className="chat-header">
            <div className="header-left">
              <div className={`bot-avatar ${isSpeaking ? 'speaking' : ''}`}>
                <img src="/robot_avatar.png" alt="Bot" onError={(e) => { e.target.src = 'https://cdn-icons-png.flaticon.com/512/4712/4712109.png' }} />
              </div>
              <div>
                <h2 className="header-info" style={{ color: 'white', margin: 0, fontSize: '1.1rem', letterSpacing: '0.5px' }}>ICETTACS 2026</h2>
                <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: 'var(--primary)', fontWeight: 500 }}>Orbit</p>
              </div>
            </div>

            <div className="header-actions">
              <button className={`icon-btn ${isVoiceEnabled ? 'speaker-active' : ''}`} onClick={toggleVoice} title="Toggle Bot Voice">
                {!isVoiceEnabled ? (
                  <svg viewBox="0 0 24 24">
                    <path d="M12,4L9.91,6.09L12,8.18M4.27,3L3,4.27L7.73,9H3V15H7L12,20V13.27L16.25,17.53C15.58,18.04 14.83,18.46 14,18.7V20.77C15.38,20.45 16.63,19.82 17.68,18.96L19.73,21L21,19.73L12,10.73M19,12C19,12.94 18.8,13.82 18.46,14.64L19.97,16.15C20.62,14.91 21,13.5 21,12C21,7.72 18,4.14 14,3.23V5.29C16.89,6.15 19,8.83 19,12M16.5,12C16.5,10.23 15.5,8.71 14,7.97V10.18L16.45,12.63C16.5,12.43 16.5,12.21 16.5,12Z" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24">
                    <path d="M14,3.23V5.29C16.89,6.15 19,8.83 19,12C19,15.17 16.89,17.84 14,18.7V20.77C18,19.86 21,16.28 21,12C21,7.72 18,4.14 14,3.23M16.5,12C16.5,10.23 15.5,8.71 14,7.97V16C15.5,15.29 16.5,13.76 16.5,12M3,9V15H7L12,20V4L7,9H3Z" />
                  </svg>
                )}
              </button>
              <button className="icon-btn close-btn" onClick={toggleChat}>×</button>
            </div>
          </div>

          <div className="chat-messages">
            {messages.map((msg, index) => (
              <div key={index} className={`message ${msg.sender === 'bot' ? 'msg-bot' : 'msg-user'}`}>
                <div dangerouslySetInnerHTML={{ __html: msg.text }} />
                {msg.suggestions && (
                  <div className="suggestions">
                    {msg.suggestions.map((sug, i) => (
                      <button key={i} className="suggestion-chip" onClick={() => handleSuggestionClick(sug)}>
                        {sug}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {isTyping && (
              <div className="typing-indicator">
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="chat-input-container">
            <button className={`mic-btn ${isRecording ? 'recording' : ''}`} onClick={toggleMic} title="Click to Speak">
              <svg viewBox="0 0 24 24">
                <path d="M12,2A3,3 0 0,1 15,5V11A3,3 0 0,1 12,14A3,3 0 0,1 9,11V5A3,3 0 0,1 12,2M19,11C19,14.53 16.39,17.44 13,17.93V21H11V17.93C7.61,17.44 5,14.53 5,11H7A5,5 0 0,0 12,16A5,5 0 0,0 17,11H19Z" />
              </svg>
            </button>
            <input
              type="text"
              className="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={isRecording ? "Listening..." : "Type or speak a question..."}
            />
            <button className="send-btn" onClick={() => handleSendMessageFinal(input)}>
              <svg viewBox="0 0 24 24">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
              </svg>
            </button>
          </div>
        </div>
      )}

      <button className="chat-trigger-btn" onClick={toggleChat}>
        💬 CHAT
      </button>
    </>
  );
}
